import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import {
  Search, Download, Layers, DollarSign, Store, 
  PieChart as PieChartIcon, Calendar, X, TrendingUp, TrendingDown,
  Wallet, Receipt, Calculator, CalendarClock, ChevronDown
} from "lucide-react";
import Sidebar from "../components/Sidebar";

// Reporting & Export Libraries
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#06b6d4', '#ec4899'];

// --- PREMIUM PDF EXPORT ---
// Added `action` parameter to handle both downloading and previewing
const exportProfitLossPDF = async (summaryBlocks, ledgerData, title, chartElementId = null, action = 'download') => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  
  const renderHeader = (pageTitle, yPos = 17) => {
    doc.setFillColor(15, 23, 42); // Slate 900
    doc.rect(0, 0, 297, 25, 'F');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(`SHRUTI ERP: ${pageTitle.toUpperCase()}`, 14, yPos);
    
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}`, 220, 15);
  };

  renderHeader(title);
  let startY = 35;

  if (chartElementId) {
    const chartEl = document.getElementById(chartElementId);
    if (chartEl) {
      try {
        const canvas = await html2canvas(chartEl, { scale: 2, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');
        doc.addImage(imgData, 'PNG', 160, 30, 110, 75); 
      } catch (err) {
        console.error("Failed to capture chart for PDF", err);
      }
    }
  }

  if (summaryBlocks.length > 0) {
    autoTable(doc, {
      head: [['P&L Metric', 'Calculated Value']],
      body: summaryBlocks.map(b => [b.label, b.value]),
      startY: startY,
      tableWidth: 130,
      margin: { left: 14 },
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 5, textColor: [15, 23, 42] },
      headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 250] }
    });
    startY = Math.max(doc.lastAutoTable.finalY + 15, 115);
  } else {
    startY = 40;
  }

  autoTable(doc, {
    head: [['Store / Entity', 'Total Revenue', 'Total Salaries', 'Other Expenses', 'Total Round Off', 'Net Profit/Loss']],
    body: ledgerData,
    startY: startY,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4, textColor: [51, 65, 85] },
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { bottom: 15 }
  });

  // Handle Preview vs Download
  if (action === 'preview') {
    const blob = doc.output('blob');
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  } else {
    doc.save(`${title.replace(/\s+/g, '_')}_Report.pdf`);
  }
};

// --- EXCEL EXPORT HELPER ---
const exportToExcel = (ledgerData, title, summary = []) => {
  const workbook = XLSX.utils.book_new();
  
  const summaryData = [
    [`SHRUTI ERP - ${title.toUpperCase()}`],
    [`Generated on: ${new Date().toLocaleString()}`],
    [],
    ["Metric", "Value"],
    ...summary.map(s => [s.label, s.value]),
    [],
    ['Entity Name', 'Total Revenue', 'Total Salaries', 'Other Expenses', 'Total Round Off', 'Net Profit/Loss'],
    ...ledgerData
  ];
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, "P&L Report");
  XLSX.writeFile(workbook, `${title.replace(/\s+/g, '_')}_Report.xlsx`);
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs font-bold">
        <p className="mb-1 text-slate-300">{payload[0].name}</p>
        <p className="text-emerald-400 text-sm">₹ {payload[0].value.toLocaleString()}</p>
      </div>
    );
  }
  return null;
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function ProfitLoss() {
  const [activeTab, setActiveTab] = useState("combined-summary");
  const [isLoading, setIsLoading] = useState(true);
  
  const [rawStoresData, setRawStoresData] = useState([]);
  
  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch all data
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const storesSnapshot = await getDocs(collection(db, "stores"));
        const storesList = storesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fullData = [];

        for (const store of storesList) {
          const salesSnap = await getDocs(collection(db, "stores", store.id, "sales"));
          const expensesSnap = await getDocs(collection(db, "stores", store.id, "expenses"));

          fullData.push({
            storeId: store.id,
            storeName: store.storeId || store.name || `Store ${store.id}`,
            sales: salesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
            expenses: expensesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          });
        }
        setRawStoresData(fullData);
      } catch (error) {
        console.error("Error fetching P&L data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // --- QUICK DATE FILTERS ---
  const applyQuickFilter = (type) => {
    const today = new Date();
    let start = new Date();
    
    if (type === 'last30') {
      start.setDate(today.getDate() - 30);
    } else if (type === 'last6months') {
      start.setMonth(today.getMonth() - 6);
    } else if (type === 'lastYear') {
      start.setFullYear(today.getFullYear() - 1);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  // --- DATE FILTERING LOGIC ---
  const storesData = useMemo(() => {
    if (!rawStoresData.length) return [];

    return rawStoresData.map(store => {
      // Filter Sales
      const filteredSales = store.sales.filter(sale => {
        if (!sale.timestamp) return true;
        const saleDate = sale.timestamp.split("T")[0];
        if (startDate && saleDate < startDate) return false;
        if (endDate && saleDate > endDate) return false;
        return true;
      });

      // Filter Expenses & Salaries
      const filteredExpenses = store.expenses.filter(exp => {
        const expDate = exp.date || (exp.timestamp ? exp.timestamp.split("T")[0] : null);
        if (!expDate) return true;
        if (startDate && expDate < startDate) return false;
        if (endDate && expDate > endDate) return false;
        return true;
      });

      return {
        ...store,
        sales: filteredSales,
        expenses: filteredExpenses
      };
    });
  }, [rawStoresData, startDate, endDate]);

  // --- AGGREGATION HELPERS ---
  const calculateStoreFinancials = (store) => {
    let totalRevenue = 0;
    let totalRoundOff = 0;
    let totalSalaries = 0;
    let totalOtherExpenses = 0;

    store.sales.forEach(sale => {
      totalRevenue += Number(sale.financials?.netPayable || 0);
      totalRoundOff += Number(sale.checkout?.roundOff || 0);
    });

    store.expenses.forEach(exp => {
      const amt = Number(exp.amount || 0);
      if (exp.category?.toLowerCase().includes("salar") || exp.title?.toLowerCase().includes("payroll")) {
        totalSalaries += amt;
      } else {
        totalOtherExpenses += amt;
      }
    });

    const totalDeductions = totalSalaries + totalOtherExpenses;
    const netProfit = totalRevenue - totalDeductions;

    return { totalRevenue, totalRoundOff, totalSalaries, totalOtherExpenses, totalDeductions, netProfit };
  };

  // --- GLOBAL AGGREGATION ---
  const globalSummary = useMemo(() => {
    let totals = { revenue: 0, roundOff: 0, salaries: 0, otherExpenses: 0, deductions: 0, profit: 0 };
    const storeBreakdown = [];

    storesData.forEach(store => {
      const stats = calculateStoreFinancials(store);
      totals.revenue += stats.totalRevenue;
      totals.roundOff += stats.totalRoundOff;
      totals.salaries += stats.totalSalaries;
      totals.otherExpenses += stats.totalOtherExpenses;
      totals.deductions += stats.totalDeductions;
      totals.profit += stats.netProfit;

      storeBreakdown.push({
        name: store.storeName,
        revenue: stats.totalRevenue,
        profit: stats.netProfit,
        ...stats
      });
    });

    const expenseDistribution = [
      { name: "Salaries Paid", value: totals.salaries },
      { name: "Other Expenses", value: totals.otherExpenses },
    ];

    return { ...totals, storeBreakdown, expenseDistribution };
  }, [storesData]);

  const globalSummaryBlocks = [
    { label: "Date Range", value: startDate && endDate ? `${startDate} to ${endDate}` : "All Time" },
    { label: "Global Gross Revenue", value: `Rs. ${globalSummary.revenue.toLocaleString()}` },
    { label: "Total Salaries Paid", value: `Rs. ${globalSummary.salaries.toLocaleString()}` },
    { label: "Total Operating Expenses", value: `Rs. ${globalSummary.otherExpenses.toLocaleString()}` },
    { label: "Accumulated Round Off", value: `Rs. ${globalSummary.roundOff.toLocaleString()}` },
    { label: "NET PROFIT / LOSS", value: `Rs. ${globalSummary.profit.toLocaleString()}` }
  ];

  // --- EXPORT HANDLERS ---
  const handleGlobalExport = (format, pdfAction = 'download') => {
    const ledgerData = globalSummary.storeBreakdown.map(s => [
      s.name, `₹${s.revenue}`, `₹${s.salaries}`, `₹${s.otherExpenses}`, `₹${s.totalRoundOff}`, `₹${s.profit}`
    ]);

    if (format === 'pdf') {
      exportProfitLossPDF(globalSummaryBlocks, ledgerData, "Global P&L Report", "global-expense-chart", pdfAction);
    } else {
      exportToExcel(ledgerData, "Global P&L Report", globalSummaryBlocks);
    }
  };

  const handleStoreExport = (format, store, stats, pdfAction = 'download') => {
    const storeSummaryBlocks = [
      { label: "Store Name", value: store.storeName },
      { label: "Date Range", value: startDate && endDate ? `${startDate} to ${endDate}` : "All Time" },
      { label: "Gross Revenue", value: `Rs. ${stats.totalRevenue.toLocaleString()}` },
      { label: "Salaries Paid", value: `Rs. ${stats.totalSalaries.toLocaleString()}` },
      { label: "Operating Expenses", value: `Rs. ${stats.totalOtherExpenses.toLocaleString()}` },
      { label: "NET PROFIT / LOSS", value: `Rs. ${stats.netProfit.toLocaleString()}` }
    ];

    const ledgerData = [[
      store.storeName, `₹${stats.totalRevenue}`, `₹${stats.totalSalaries}`, `₹${stats.totalOtherExpenses}`, `₹${stats.totalRoundOff}`, `₹${stats.netProfit}`
    ]];

    if (format === 'pdf') {
      exportProfitLossPDF(storeSummaryBlocks, ledgerData, `${store.storeName} P&L Report`, `store-expense-chart-${store.storeId}`, pdfAction);
    } else {
      exportToExcel(ledgerData, `${store.storeName} P&L Report`, storeSummaryBlocks);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200">
      <Sidebar />

      <div className="flex-1 p-4 lg:p-8 overflow-x-hidden space-y-6">
        
        {/* HEADER & FILTERS */}
        <div className="bg-white border border-slate-200 p-5 rounded-3xl shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100 pb-4">
            <div>
              <h1 className="text-xl font-black uppercase text-slate-800 flex items-center gap-3">
                <Calculator className="text-blue-600" size={24} /> 
                Profit & Loss Ledger
              </h1>
              <p className="text-slate-500 text-xs font-bold mt-1 tracking-wider">Automated financial tracking and net profit analysis</p>
            </div>
            
            {/* DATE FILTER UI */}
            <div className="flex flex-wrap items-center gap-3">
               <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
                 <Calendar size={14} className="text-slate-400"/>
                 <span>From:</span>
                 <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer" />
               </div>
               <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
                 <Calendar size={14} className="text-slate-400"/>
                 <span>To:</span>
                 <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer" />
               </div>
               {(startDate || endDate) && (
                 <button onClick={() => { setStartDate(""); setEndDate(""); }} className="px-3 py-2 bg-rose-50 hover:bg-rose-100 hover:text-rose-600 text-rose-500 font-bold text-xs rounded-xl transition flex items-center gap-1">
                   <X size={14} /> Clear
                 </button>
               )}
            </div>
          </div>
          
          {/* QUICK PRESETS */}
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-black uppercase text-slate-400 flex items-center gap-1.5 mr-2"><CalendarClock size={14}/> Quick Ranges:</span>
            <button onClick={() => applyQuickFilter('last30')} className="px-4 py-1.5 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 text-slate-600 text-[10px] font-bold uppercase rounded-lg transition-colors border border-slate-200">Last 30 Days</button>
            <button onClick={() => applyQuickFilter('last6months')} className="px-4 py-1.5 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 text-slate-600 text-[10px] font-bold uppercase rounded-lg transition-colors border border-slate-200">Last 6 Months</button>
            <button onClick={() => applyQuickFilter('lastYear')} className="px-4 py-1.5 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 text-slate-600 text-[10px] font-bold uppercase rounded-lg transition-colors border border-slate-200">Last 1 Year</button>
          </div>
        </div>

        {/* TOP NAVIGATION TABS */}
        <div className="flex flex-wrap gap-2 p-1.5 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <button onClick={() => setActiveTab("combined-summary")} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all font-bold text-xs ${activeTab === "combined-summary" ? "bg-blue-600 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}>
            <Layers size={16} /> Global P&L Overview
          </button>
          <div className="w-px h-8 bg-slate-200 mx-2 self-center hidden sm:block"></div>
          {storesData.map(store => (
            <button key={store.storeId} onClick={() => setActiveTab(store.storeId)} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all font-bold text-xs ${activeTab === store.storeId ? "bg-slate-800 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}>
              <Store size={16} /> {store.storeName}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="space-y-6">
              
              {/* =========================================
                  TAB: GLOBAL P&L OVERVIEW (ALL STORES)
                  ========================================= */}
              {activeTab === "combined-summary" && (
                <>
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-lg font-black uppercase text-slate-800">Master Financial Dashboard</h2>
                    <div className="flex gap-2 relative">
                      
                      {/* CSS-ONLY PDF DROPDOWN */}
                      <div className="relative group">
                        <button className="px-5 py-2.5 bg-rose-50 text-rose-600 border border-rose-200 rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm cursor-default">
                          <Download size={14}/> PDF Options <ChevronDown size={14} />
                        </button>
                        <div className="absolute right-0 top-full mt-2 w-36 bg-white border border-slate-200 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden flex flex-col">
                          <button onClick={() => handleGlobalExport('pdf', 'preview')} className="text-left px-4 py-3 text-xs font-bold text-slate-700 hover:bg-rose-50 hover:text-rose-600 border-b border-slate-100 transition-colors">Preview PDF</button>
                          <button onClick={() => handleGlobalExport('pdf', 'download')} className="text-left px-4 py-3 text-xs font-bold text-slate-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">Export PDF</button>
                        </div>
                      </div>

                      <button onClick={() => handleGlobalExport('excel')} className="px-5 py-2.5 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm"><Download size={14}/> Export Excel</button>
                    </div>
                  </div>

                  {/* Summary KPI Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                      <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Wallet size={14}/> Global Revenue</div>
                      <div className="text-3xl font-black text-blue-600">₹{globalSummary.revenue.toLocaleString()}</div>
                    </div>
                    <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                      <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Receipt size={14}/> Operational Expenses</div>
                      <div className="text-3xl font-black text-rose-500">₹{globalSummary.deductions.toLocaleString()}</div>
                    </div>
                    <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                      <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Calculator size={14}/> Total Round-Off Applied</div>
                      <div className="text-3xl font-black text-slate-600">₹{globalSummary.roundOff.toLocaleString()}</div>
                    </div>
                    <div className={`border ${globalSummary.profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'} p-6 rounded-3xl shadow-sm flex flex-col justify-center relative overflow-hidden`}>
                      <div className={`text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2 ${globalSummary.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {globalSummary.profit >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>} 
                        Net {globalSummary.profit >= 0 ? 'Profit' : 'Loss'}
                      </div>
                      <div className={`text-3xl font-black ${globalSummary.profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        ₹{Math.abs(globalSummary.profit).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Expense Distribution Chart */}
                    <div className="lg:col-span-2 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm h-[400px] flex flex-col">
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-2 text-center border-b border-slate-100 pb-3">Deduction Distribution (Salaries vs Exp)</h3>
                      <div id="global-expense-chart" className="flex-1 w-full bg-white p-2 mt-2">
                        {globalSummary.deductions > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={globalSummary.expenseDistribution} cx="50%" cy="50%" innerRadius={70} outerRadius={100} fill="#8884d8" paddingAngle={5} dataKey="value">
                                {globalSummary.expenseDistribution.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                              </Pie>
                              <RechartsTooltip content={<CustomTooltip />} />
                              <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold' }} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex items-center justify-center text-slate-400 font-bold text-xs">No expenses recorded for selected period.</div>
                        )}
                      </div>
                    </div>

                    {/* Store P&L Breakdown Table */}
                    <div className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col">
                      <h3 className="text-xs font-black uppercase text-slate-800 mb-4 border-b border-slate-100 pb-3">Branch-wise Profitability</h3>
                      <div className="overflow-x-auto flex-1 custom-scrollbar">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0 font-bold">
                            <tr className="border-b border-slate-200">
                              <th className="p-4">Store Name</th>
                              <th className="p-4 text-right text-blue-600">Revenue</th>
                              <th className="p-4 text-right text-rose-500">Deductions</th>
                              <th className="p-4 text-right">Net P&L</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {globalSummary.storeBreakdown.map((s, i) => (
                              <tr key={i} className="hover:bg-slate-50">
                                <td className="p-4 font-bold text-slate-700">{s.name}</td>
                                <td className="p-4 font-mono font-bold text-blue-600 text-right">₹{s.revenue.toLocaleString()}</td>
                                <td className="p-4 font-mono font-bold text-rose-500 text-right">₹{s.totalDeductions.toLocaleString()}</td>
                                <td className="p-4 font-mono font-black text-right">
                                  <span className={`px-2 py-1 rounded-lg ${s.profit >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                    {s.profit >= 0 ? '+' : '-'}₹{Math.abs(s.profit).toLocaleString()}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* =========================================
                  TAB: INDIVIDUAL STORE REPORTS
                  ========================================= */}
              {activeTab !== "combined-summary" && storesData.map(store => {
                if (store.storeId !== activeTab) return null;
                
                const stats = calculateStoreFinancials(store);
                const expenseDistribution = [
                  { name: "Salaries Paid", value: stats.totalSalaries },
                  { name: "Other Expenses", value: stats.totalOtherExpenses },
                ];

                return (
                  <div key={store.storeId} className="space-y-6">
                    
                    {/* Header & Export Actions */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <h2 className="text-lg font-black uppercase text-slate-800 flex items-center gap-2">
                        <Store className="text-slate-600" size={20}/> {store.storeName} Financials
                      </h2>
                      <div className="flex gap-2">
                        
                        {/* CSS-ONLY PDF DROPDOWN FOR STORE */}
                        <div className="relative group">
                          <button className="px-5 py-2.5 bg-rose-50 text-rose-600 border border-rose-200 rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm cursor-default">
                            <Download size={14}/> PDF Options <ChevronDown size={14} />
                          </button>
                          <div className="absolute right-0 top-full mt-2 w-36 bg-white border border-slate-200 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden flex flex-col">
                            <button onClick={() => handleStoreExport('pdf', store, stats, 'preview')} className="text-left px-4 py-3 text-xs font-bold text-slate-700 hover:bg-rose-50 hover:text-rose-600 border-b border-slate-100 transition-colors">Preview PDF</button>
                            <button onClick={() => handleStoreExport('pdf', store, stats, 'download')} className="text-left px-4 py-3 text-xs font-bold text-slate-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">Export PDF</button>
                          </div>
                        </div>

                        <button onClick={() => handleStoreExport('excel', store, stats)} className="px-5 py-2.5 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm"><Download size={14}/> Store Excel</button>
                      </div>
                    </div>

                    {/* Store KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                        <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Wallet size={14}/> Total Revenue</div>
                        <div className="text-3xl font-black text-blue-600">₹{stats.totalRevenue.toLocaleString()}</div>
                      </div>
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                        <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Receipt size={14}/> Store Deductions</div>
                        <div className="text-3xl font-black text-rose-500">₹{stats.totalDeductions.toLocaleString()}</div>
                      </div>
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                        <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Calculator size={14}/> Applied Round-Off</div>
                        <div className="text-3xl font-black text-slate-600">₹{stats.totalRoundOff.toLocaleString()}</div>
                      </div>
                      <div className={`border ${stats.netProfit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'} p-6 rounded-3xl shadow-sm flex flex-col justify-center relative overflow-hidden`}>
                        <div className={`text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2 ${stats.netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {stats.netProfit >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>} 
                          Store Net {stats.netProfit >= 0 ? 'Profit' : 'Loss'}
                        </div>
                        <div className={`text-3xl font-black ${stats.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          ₹{Math.abs(stats.netProfit).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* Chart & Tables */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Store Deduction Chart */}
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm h-[400px] flex flex-col">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-2 text-center border-b border-slate-100 pb-3">Expense Breakdown</h3>
                        <div id={`store-expense-chart-${store.storeId}`} className="flex-1 w-full bg-white p-2 mt-2">
                          {stats.totalDeductions > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={expenseDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={90} fill="#8884d8" paddingAngle={5} dataKey="value">
                                  {expenseDistribution.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                </Pie>
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold' }} />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="h-full flex items-center justify-center text-slate-400 font-bold text-xs">No expenses logged.</div>
                          )}
                        </div>
                      </div>

                      {/* Detail Ledger Column */}
                      <div className="lg:col-span-2 flex flex-col gap-6">
                        
                        {/* Summary Data Table */}
                        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm flex flex-col overflow-hidden">
                          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-center gap-4">
                            <h3 className="text-sm font-black uppercase text-slate-800">Financial Metrics Matrix</h3>
                          </div>
                          <div className="p-6">
                            <table className="w-full text-left border-collapse text-sm">
                              <tbody className="divide-y divide-slate-100 font-medium">
                                <tr className="hover:bg-slate-50">
                                  <td className="p-4 font-bold text-slate-600">Total Sales Revenue</td>
                                  <td className="p-4 font-mono font-black text-blue-600 text-right">₹{stats.totalRevenue.toLocaleString()}</td>
                                </tr>
                                <tr className="hover:bg-slate-50">
                                  <td className="p-4 font-bold text-slate-600">Total Employee Salaries</td>
                                  <td className="p-4 font-mono font-bold text-rose-500 text-right">- ₹{stats.totalSalaries.toLocaleString()}</td>
                                </tr>
                                <tr className="hover:bg-slate-50">
                                  <td className="p-4 font-bold text-slate-600">General Operating Expenses</td>
                                  <td className="p-4 font-mono font-bold text-rose-500 text-right">- ₹{stats.totalOtherExpenses.toLocaleString()}</td>
                                </tr>
                                <tr className="bg-slate-50 border-t-2 border-slate-200">
                                  <td className="p-4 font-black uppercase text-slate-800">Final Net Return</td>
                                  <td className={`p-4 font-mono font-black text-right text-lg ${stats.netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                    {stats.netProfit >= 0 ? '+' : '-'} ₹{Math.abs(stats.netProfit).toLocaleString()}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}